/**
 * Authoritative "date under review" notice (T010-FE, plan R7).
 *
 * Rendered when the by-ID API answered the structured 409 contract owned by
 * `api/lib/event-date-review.ts` (T009-BE): `{ code: 'DATE_UNDER_REVIEW' }`.
 * The event still exists — its disputed date is being verified — so the
 * detail view withholds the event instead of advertising a date that may be
 * wrong. This is deliberately NOT the deleted/not-found state: the
 * page/modal stays open, explains, and never silently collapses to null.
 *
 * Presentational only: it owns no styles. The caller passes the layout and
 * typography classes of the surface it draws on (the EventPage error block,
 * the EventModal body, the saved list's held-favorite box), so each surface
 * keeps its established look and no new design-system surface is introduced.
 */
export const DATE_UNDER_REVIEW_MESSAGE = 'La data di questo evento è in verifica.';

interface DateUnderReviewNoticeProps {
  /** Outer wrapper class — the caller's existing error/body layout. */
  className?: string;
  /** Heading class — the caller's existing title style. */
  messageClassName?: string;
  /** Optional heading id, for a caller that labels its dialog with it. */
  messageId?: string;
  /** Heading level (default 1). Lists embedding several notices use 2+. */
  headingLevel?: 1 | 2 | 3;
}

export function DateUnderReviewNotice({
  className,
  messageClassName,
  messageId,
  headingLevel = 1,
}: DateUnderReviewNoticeProps) {
  const Heading: 'h1' | 'h2' | 'h3' = headingLevel === 2 ? 'h2' : headingLevel === 3 ? 'h3' : 'h1';
  return (
    <div className={className} role="status" data-testid="date-under-review-notice">
      <Heading id={messageId} className={messageClassName}>
        {DATE_UNDER_REVIEW_MESSAGE}
      </Heading>
    </div>
  );
}
